import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/breadcrumb.js';
import '../components/closable_callout.js';
import '../components/icon.js';

import { css, html, LitElement, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
    createOrganization,
    isOrganizationType,
    type OrganizationType,
} from '../libraries/api_client/organization.js';
import { notifyOrganizationChanged } from '../libraries/events.js';
import { FormController } from '../libraries/form_controller.js';
import { type ControlIdentity, type IRadioFormField } from '../libraries/form_field_types.js';
import { readField } from '../libraries/form_field_walk.js';
import { formHostStyles } from '../libraries/form_host_styles.js';
import { formOutcomeOf, type FormSubmitResult, formSubmitted } from '../libraries/form_outcome.js';
import { nameAndLegalFormFields, readOrganizationData } from '../libraries/form_presets.js';
import { redirectTo } from '../libraries/navigation.js';
import {
    organizationTypeDescription,
    organizationTypeIcon,
    organizationTypeLabel,
} from '../libraries/organization_type.js';
import { pageHeadingStyles } from '../libraries/page_heading.js';

/** The organisation types in the order the choice offers them. */
const typeOptionOrder: ReadonlyArray<OrganizationType> = [
    'consulting',
    'management',
];

/** The choice's control, written once and spread into the field its reader goes with. */
const typeControl: ControlIdentity = {
    name: 'type',
};

/**
 * The type the form opens on, which is also what a submission naming an unknown one
 * falls back to. Consulting, because it is the type that needs no farms of its own.
 */
const defaultOrganizationType: OrganizationType = 'consulting';

@customElement('fm-organization-create')
export class FmOrganizationCreate extends LitElement {

    static styles = [
        formHostStyles,
        pageHeadingStyles,
        css`
            :host {
                display: block;
            }

            wa-radio-group[name='type']::part(form-control-input) {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: var(--wa-space-m);
            }

            wa-radio-group[name='type'] wa-radio {
                display: flex;
                flex-direction: column;
                align-items: stretch;
                gap: 0.375rem;
                margin: 0;
                padding: 0.875rem var(--wa-space-m);
                background: var(--fm-color-surface);
                border: var(--wa-border-width-s) solid var(--fm-color-border-strong);
                border-radius: var(--wa-border-radius-m);
                cursor: pointer;
            }

            wa-radio-group[name='type'] wa-radio::part(control) {
                display: none;
            }

            wa-radio-group[name='type'] wa-radio:hover {
                background: var(--fm-color-hover-surface);
            }

            wa-radio-group[name='type'] wa-radio[aria-checked='true'] {
                padding: 0.8125rem 0.9375rem;
                background: var(--fm-color-brand-background);
                border-width: var(--wa-border-width-m);
                border-color: var(--fm-color-brand-loud);
            }

            wa-radio-group[name='type'] wa-radio:focus-visible {
                outline: var(--wa-focus-ring);
                outline-offset: var(--wa-focus-ring-offset);
            }

            .type-card-header {
                display: flex;
                align-items: center;
                gap: var(--wa-space-xs);
            }

            .type-card-icon {
                color: var(--fm-color-text-normal);
            }

            .type-card-title {
                font-size: var(--wa-font-size-s);
                font-weight: var(--wa-font-weight-semibold);
                color: var(--fm-color-text-normal);
            }

            .type-card-description {
                font-size: var(--wa-font-size-xs);
                color: var(--fm-color-text-quiet);
            }

            form .row {
                grid-template-columns: 6.25rem 1fr;
            }

            form .row-street {
                grid-template-columns: 1fr 6.25rem;
            }

            @container form-card (max-width: 30rem) {
                wa-radio-group[name='type']::part(form-control-input) {
                    grid-template-columns: 1fr;
                }
            }
        `,
    ];

    /**
     * The two organisation types, as an ordinary `radio` field whose options draw
     * themselves.
     *
     * The cards are this page's treatment; the keyboard model is not, which is what
     * drawing the group by hand would cost. `<wa-radio-group>` owns the arrow-key roving
     * focus and the single tab stop a radio group is supposed to have.
     * @returns The declaration.
     */
    private static typeField (): IRadioFormField {
        return {
            kind: 'radio',
            ...typeControl,
            label: 'Organisationstyp',
            hint: 'Wählen Sie, wofür Sie diese Organisation nutzen.',
            required: true,
            value: defaultOrganizationType,
            options: typeOptionOrder.map(type => ({
                value: type,
                label: organizationTypeLabel(type),
                labelContent: (): TemplateResult => FmOrganizationCreate.typeCard(type),
            })),
        };
    }

    /**
     * One option's card.
     * @param type - The organisation type it offers.
     * @returns The card.
     */
    private static typeCard (type: OrganizationType): TemplateResult {
        return html`
            <span class="type-card-header">
                <fm-icon class="type-card-icon" .icon=${organizationTypeIcon(type)}></fm-icon>
                <span class="type-card-title">${organizationTypeLabel(type)}</span>
            </span>
            <span class="type-card-description">${organizationTypeDescription(type)}</span>
        `;
    }

    private readonly form = new FormController(this, () => ({
        fields: [
            FmOrganizationCreate.typeField(),
            ...nameAndLegalFormFields(undefined, 'z.B. Beratungsring SH'),
            {
                kind: 'address',
                placeholders: {
                    streetName: 'z.B. Grüner Kamp',
                    houseNumber: 'Nr.',
                    postalCode: 'z.B. 24768',
                    city: 'z.B. Rendsburg',
                },
            },
        ],
        submitLabel: 'Anlegen',
        onCancel: (): void => this.handleCancel(),
        submit: async (formData: FormData): Promise<FormSubmitResult> => this.createFrom(formData),
    }));

    /**
     * Creates the organisation and moves on to it.
     * @param formData - The submitted values.
     * @returns What the request reported.
     */
    private async createFrom (formData: FormData): Promise<FormSubmitResult> {
        const submitted = readField(formData, typeControl);
        const type: OrganizationType = isOrganizationType(submitted) ? submitted : defaultOrganizationType;
        const result = await createOrganization(readOrganizationData(formData), type);

        return formOutcomeOf(result, (organization): FormSubmitResult => {
            notifyOrganizationChanged(organization.id);

            redirectTo(this.landingPathFor(type, organization.id));

            return formSubmitted;
        });
    }

    private landingPathFor (type: OrganizationType, organizationId: string): string {
        const base = `/organizations/${organizationId}`;

        // A managing organisation continues onboarding into its farms tab, whose
        // empty state offers "Betrieb anlegen"; a consulting organisation advises
        // external farms, so creating an own farm is never forced.
        return type === 'management' ? `${base}/farms` : base;
    }

    private readonly handleCancel = (): void => {
        redirectTo('/organizations');
    };

    render (): TemplateResult {
        return html`
            <fm-breadcrumb .items=${[
                {
                    label: 'Alle',
                    href: '/organizations',
                },
                {
                    label: 'Organisation anlegen',
                },
            ]}></fm-breadcrumb>
            <h1>Organisation anlegen</h1>
            <p class="description">
                Legen Sie eine neue Organisation an und wählen Sie, ob sie Betriebe verwaltet
                oder berät. Der Organisationstyp kann später nicht mehr verändert werden.
            </p>

            ${this.form.render()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-organization-create': FmOrganizationCreate;
    }
}
